import PropTypes from 'prop-types'
import React, { useContext, useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getStationTypeFromUserConstants } from '../../../../../utils/StationUtils'
import UserAction from '../../actions/UserAction'
import AdministrationAction from '../../../../actions/AdministrationAction'
import WaitAction from 'wait/WaitAction'
import {
    ALL,
    CATCHMENT,
    DISTRIBUTION,
    HYDRO,
    INSTALLATION,
    MAT, PERIMETERS, PGSSE, PIEZO,
    PLUVIO,
    PRODUCTION,
    QUALITO, RESOURCE, RPQS, SITUATION,
} from '../../constants/ApplicationHabilitationConstants'
import {
    AGRI,
    AQUADB,
    OBSERVATORY,
    QAPTYS,
    SITUATION_DIAGNOSTIC_FORAGE,
} from '../../constants/HabilitationConstants'
import DtoUserHabilitation from '../../dto/DtoUserHabilitation'
import { getI18nOrLabel } from 'utils/StringUtil'
import i18n from 'simple-react-i18n'
import Checkbox from 'components/forms/Checkbox'
import { Accordion, AccordionDetails, Grid2 } from '@mui/material'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import { NAME_LOGIN_PICTURE } from 'administration/constants/AdministrationConstants'
import { AccordionTitle } from 'components/styled/Accordions'
import { ModuleHabsContext, ModuleHabsDispatchContext } from 'administration/components/modules/AdminModulesApp'
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'

const disabledHabilitations = [SITUATION_DIAGNOSTIC_FORAGE]

const ModuleCard = ({
    moduleType = '',
}) => {
    const dispatch = useDispatch()
    const {
        applicationHabilitations,
        userHabilitations,
        user,
        applicativeUsers,
        contacts,
    } = useSelector(store => ({
        applicationHabilitations: store.UserReducer.applicationHabilitations,
        userHabilitations: store.UserReducer.userHabilitations,
        user: store.UserReducer.user,
        applicativeUsers: store.UserReducer.applicativeUsers,
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const moduleHabs = useContext(ModuleHabsContext)
    const moduleHabsDispatch = useContext(ModuleHabsDispatchContext)

    useEffect(() => {
        if (!!moduleHabs && !moduleHabs.habilitationsUser?.length) {
            moduleHabsDispatch({ type: 'habilitationsUser', payload: userHabilitations })
        }
    }, [userHabilitations])

    const habilitationsOfUser = moduleHabs?.habilitationsUser ?? userHabilitations

    const habilitations = applicationHabilitations.filter(hab => hab.habilitationType === moduleType)
    const filteredHabilitations = habilitations.filter(hab => !!hab.title && !hab.parameter.includes(`_${ALL}`))

    const hasHabilitation = hab => habilitationsOfUser.some(h => h.habilitation === hab)

    const hasModuleHabilitation = hasHabilitation(moduleType)

    const isAllCheck = filteredHabilitations.every(hab => hasHabilitation(hab.parameter))


    const onChangeApplicativeUser = () => {
        const contact = contacts.find(c => c.id === user.contactCode)
        const newUser = {
            ...user,
            contactName: contact?.name || '',
        }

        dispatch(UserAction.updateUser(newUser)).then(() => {
            dispatch(AdministrationAction.fetchPublicPicture(newUser.login, NAME_LOGIN_PICTURE))
            dispatch(WaitAction.waitStart())
            dispatch(UserAction.fetchUsers()).then(() => {
                dispatch(WaitAction.waitStop())
            })
        })
    }

    const onChange = (habilitation, isCheck) => {
        const newHabilitations = isCheck ?
            habilitationsOfUser.filter(h => h.habilitation !== habilitation) :
            [
                ...habilitationsOfUser,
                new DtoUserHabilitation({
                    login: user.login,
                    habilitation,
                }),
            ]

        if (moduleHabs?.habilitationsUser) {
            moduleHabsDispatch({ type: 'habilitationsUser', payload: newHabilitations })
        } else {
            dispatch(UserAction.updateUserHabilitations(user.login, newHabilitations))

            if (applicativeUsers.find(a => a.login === habilitation)) {
                onChangeApplicativeUser()
            }
        }
    }

    const onChangeAll = () => {
        const filteredUserHabilitations = habilitationsOfUser.filter(hab => !filteredHabilitations.some(h => h.parameter === hab.habilitation))
        const updatedHabilitations = isAllCheck ?
            filteredUserHabilitations :
            [
                ...filteredUserHabilitations,
                ...filteredHabilitations.map(hab => new DtoUserHabilitation({ login: user.login, habilitation: hab.parameter })),
            ]

        if (moduleHabs?.habilitationsUser) {
            moduleHabsDispatch({ type: 'habilitationsUser', payload: updatedHabilitations })
        } else {
            dispatch(UserAction.updateUserHabilitations(user.login, updatedHabilitations))
        }
    }

    return (
        <Accordion>
            <AccordionTitle
                title={
                    <Grid2 container alignItems='center' spacing={1}>
                        <Grid2 size={0.4}>{hasModuleHabilitation && <DoneAllRoundedIcon />}</Grid2>
                        {getI18nOrLabel(getStationTypeFromUserConstants(moduleType))}
                    </Grid2>
                }
            />
            <AccordionDetails>
                <Grid2 container>
                    <Grid2 size='auto'>
                        <Checkbox
                            label={i18n.moduleAccess}
                            checked={hasModuleHabilitation}
                            onChange={() => onChange(moduleType, hasModuleHabilitation)}
                        />
                    </Grid2>
                    {
                        !!filteredHabilitations.length && (
                            <Grid2 size={12}>
                                <StyledFieldSet>
                                    <StyledLegend>{i18n.options}</StyledLegend>
                                    <Grid2 container>
                                        <Grid2 container size={12}>
                                            <Grid2 size={6}>
                                                <Checkbox
                                                    label={i18n.selectAll}
                                                    disabled={!hasModuleHabilitation}
                                                    checked={isAllCheck}
                                                    onChange={onChangeAll}
                                                />
                                            </Grid2>
                                        </Grid2>
                                        {
                                            filteredHabilitations.map(hab => {
                                                const isCheck = hasHabilitation(hab.parameter)
                                                const isDisabled = disabledHabilitations.includes(hab.parameter)
                                                return (
                                                    <Grid2 key={hab.parameter} size={6}>
                                                        <Checkbox
                                                            label={isDisabled ? `${getI18nOrLabel(hab.title)} (${i18n.unavailable})` : getI18nOrLabel(hab.title)}
                                                            disabled={isDisabled || !hasModuleHabilitation}
                                                            checked={isCheck}
                                                            onChange={() => onChange(hab.parameter, isCheck)}
                                                        />
                                                    </Grid2>
                                                )
                                            })
                                        }
                                    </Grid2>
                                </StyledFieldSet>
                            </Grid2>
                        )
                    }
                </Grid2>
            </AccordionDetails>
        </Accordion>
    )
}

ModuleCard.propTypes = {
    moduleType: PropTypes.oneOf([
        QUALITO,
        PIEZO,
        PLUVIO,
        HYDRO,
        DISTRIBUTION,
        INSTALLATION,
        CATCHMENT,
        PRODUCTION,
        MAT,
        SITUATION,
        AGRI,
        QAPTYS,
        OBSERVATORY,
        AQUADB,
        PGSSE,
        RESOURCE,
    ]),
}

const UserModulesPanel = () => {
    const dispatch = useDispatch()
    const {
        applicativeUsers,
    } = useSelector(store => ({
        applicativeUsers: store.UserReducer.applicativeUsers,
    }), shallowEqual)

    const moduleHabs = useContext(ModuleHabsContext)

    useEffect(() => {
        if (!applicativeUsers.length) {
            dispatch(UserAction.fetchApplicativeUsers())
        }
    }, [])

    return (
        <Grid2 container direction='column' spacing={1}>
            {(!moduleHabs || moduleHabs.modules.includes(AGRI)) && <ModuleCard moduleType={AGRI} />}
            {(!moduleHabs || moduleHabs.modules.includes(AQUADB)) && <ModuleCard moduleType={AQUADB} />}
            {(!moduleHabs || moduleHabs.modules.includes(CATCHMENT)) && <ModuleCard moduleType={CATCHMENT} />}
            {(!moduleHabs || moduleHabs.modules.includes(DISTRIBUTION)) && <ModuleCard moduleType={DISTRIBUTION} />}
            {(!moduleHabs || moduleHabs.modules.includes(HYDRO)) && <ModuleCard moduleType={HYDRO} />}
            {(!moduleHabs || moduleHabs.modules.includes(INSTALLATION)) && <ModuleCard moduleType={INSTALLATION} />}
            {(!moduleHabs || moduleHabs.modules.includes(MAT)) && <ModuleCard moduleType={MAT} />}
            {(!moduleHabs || moduleHabs.modules.includes(OBSERVATORY)) && <ModuleCard moduleType={OBSERVATORY} />}
            {(!moduleHabs || moduleHabs.modules.includes(PERIMETERS)) && <ModuleCard moduleType={PERIMETERS} />}
            {(!moduleHabs || moduleHabs.modules.includes(PGSSE)) && <ModuleCard moduleType={PGSSE} />}
            {(!moduleHabs || moduleHabs.modules.includes(PIEZO)) && <ModuleCard moduleType={PIEZO} />}
            {(!moduleHabs || moduleHabs.modules.includes(PLUVIO)) && <ModuleCard moduleType={PLUVIO} />}
            {(!moduleHabs || moduleHabs.modules.includes(QAPTYS)) && <ModuleCard moduleType={QAPTYS} />}
            {(!moduleHabs || moduleHabs.modules.includes(QUALITO)) && <ModuleCard moduleType={QUALITO} />}
            {(!moduleHabs || moduleHabs.modules.includes(RESOURCE)) && <ModuleCard moduleType={RESOURCE} />}
            {(!moduleHabs || moduleHabs.modules.includes(RPQS)) && <ModuleCard moduleType={RPQS} />}
            {(!moduleHabs || moduleHabs.modules.includes(PRODUCTION)) && <ModuleCard moduleType={PRODUCTION} />}
            {(!moduleHabs || moduleHabs.modules.includes(SITUATION)) && <ModuleCard moduleType={SITUATION} />}
        </Grid2>
    )
}

UserModulesPanel.propTypes = {
    habilitationsUser: PropTypes.arrayOf(PropTypes.shape({})),
    setHabilitationsUser: PropTypes.func,
}

export default UserModulesPanel
